import React, { useState, useEffect } from 'react';
import { Table, notification } from 'antd';
import { ColumnsType } from 'antd/es/table';
import styled from 'styled-components/macro';
import { InfoMessage, Spinner } from 'components';
import { Title, Input, Body, Button, Menu, Flex } from '@cognite/cogs.js';
import { Rule, RuleRunStatus } from 'types/dt-graphql-apollo-v2';
import { getFailedInstances } from 'utils/data-validation';
import {
  addSpaceBeforeCapitalLetters,
  formatDateRecentTime,
  isObject,
} from 'utils/misc';
import {
  sortAttributesAlphabetically,
  convertToSimpleQueryFDM,
} from 'utils/schemas';
import { useStoreState } from 'hooks';
import { flattenJSON } from 'utils/flatten';
import { Project, Property, Schema } from 'hooks/projects';
import { RuleRunValidity } from 'pages/ViewDigitalTwinPage/components/Schemas/ViewSchemaPage/DataValidation/ViewRunsPage/RunsHistory';
import { InstancesDataFetch } from './components/InstancesDataFetch';

const getPrimaryKeyFromSchema = (schema?: Schema | null) =>
  schema?.properties?.find(
    (property) => property?.type === 'ID!' || property?.type === 'ID'
  )?.name || 'id';

export const RunInstancesTable = ({
  project,
  schemaCollectionRefId,
  selectedRule,
  selectedRun,
  selectedSchema,
  schemaProperties,
}: {
  project: Project;
  schemaCollectionRefId: string;
  selectedRule: Rule;
  selectedRun: RuleRunStatus;
  selectedSchema?: Schema | null;
  schemaProperties?: Array<Property>;
}) => {
  const token = useStoreState((state) => state.auth.token);
  const tenant = useStoreState((state) => state.auth.tenant);
  const cluster = useStoreState((state) => state.auth.cluster);

  const [failedInstances, setFailedInstances] = useState<Object[]>();
  const [failedInstancesIds, setFailedInstancesIds] = useState<Object[]>();
  const [tableColumnsLabels, setTableColumnsLabels] = useState<string[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [loadingDataAttributes, setLoadingDataAttributes] = useState(false);
  const [showAttributesList, setShowAttributesList] = useState(false);
  const [showLongAddButton, setShowLongAddButton] = useState(true);
  const [searchInstances, setSearchInstances] = useState<string>('');
  const [searchAttributes, setSearchAttributes] = useState<string>('');
  const [dataAttributesQuery, setDataAttributesQuery] = useState<string>('');

  const totalFailedCount = selectedRun.totalFailed || 0;

  const primaryKeyName: string =
    selectedRule.primaryKeyName || getPrimaryKeyFromSchema(selectedSchema);

  // Schema attributes available to add in the table
  const [availableSchemaProperties, setAvailableSchemaProperties] = useState<
    Array<Property>
  >([]);

  useEffect(() => {
    if (!availableSchemaProperties.length && schemaProperties) {
      setAvailableSchemaProperties(schemaProperties);
    }
  }, [schemaProperties]);

  /* If a collection report was run, the run may have extra attributes added already in the stored file.
     In this case, we need to filter out and remove these attributes fron the "Add attribute" dropdown.
  */
  useEffect(() => {
    if (availableSchemaProperties.length && tableColumnsLabels) {
      const tableHasExtraAttributes = availableSchemaProperties.some(
        (property) =>
          tableColumnsLabels.find((columnName) => columnName === property.name)
      );

      if (tableHasExtraAttributes) {
        const filteredAvailableProperties = availableSchemaProperties.filter(
          (property) => !tableColumnsLabels.includes(property.name)
        );
        setAvailableSchemaProperties(filteredAvailableProperties);
      }
    }
  }, [tableColumnsLabels]);

  useEffect(() => {
    async function fetchFailedData() {
      let response: any = null;

      response = await getFailedInstances(
        selectedRun.externalId,
        token,
        tenant,
        cluster
      );

      if (response) {
        try {
          const failedData = flattenJSON(JSON.parse(await response.text()));

          setFailedInstances(failedData);
          const failedIds = failedData.map(
            (dataObject: any) => `"${dataObject[primaryKeyName]}"`
          );
          setFailedInstancesIds(failedIds);
          if (failedData[0]) {
            setTableColumnsLabels(Object.keys(failedData[0]));
          } else {
            throw new Error('Could not fetch data for the failed instances');
          }
        } catch (error: any) {
          notification.error({
            message: 'Something went wrong while fetching the data',
            description: error.toString(),
          });
        }
      }
      setLoadingData(false);
    }

    if (!failedInstances && totalFailedCount > 0) {
      setLoadingData(true);
      fetchFailedData();
    }
  }, []);

  // Function to handle the resizing of "Add attrbute" button
  const handleButtonResize = () => {
    const width = document.getElementById('tableColumn')?.offsetWidth || 0;
    if (width <= 110) {
      setShowLongAddButton(false);
    } else {
      setShowLongAddButton(true);
    }
  };

  /* Get the newly fetched data for all the failedIds and
   add them into the failedInstances array */
  const handleQueryResult = (queryResult: any) => {
    const fetchedData: Object[] = Object.values(queryResult);

    if (failedInstances) {
      const updatedFailedInstances: Object[] = failedInstances.map(
        (instance: any) => {
          const updatedInstance = Object.values(fetchedData[0])
            .flat()
            .find(
              (fetchedInstance: any) =>
                fetchedInstance[primaryKeyName] === instance[primaryKeyName]
            );
          return {
            ...instance,
            ...updatedInstance,
          };
        }
      );
      setFailedInstances(updatedFailedInstances);
    }
    setDataAttributesQuery('');
    setLoadingDataAttributes(false);
  };

  const getFilteredInstances = (instancesToFilter: Object[] | undefined) => {
    if (!instancesToFilter || !Array.isArray(instancesToFilter)) return [];
    return instancesToFilter.filter(
      (item: Object) =>
        Object.values(item).filter(
          (value) => value && value.toString().includes(searchInstances)
        ).length > 0
    );
  };

  const getFilteredAttributes = (attributesToFilter: Array<Property>) => {
    if (!attributesToFilter) return [];
    return attributesToFilter.filter((property: Property) =>
      property.name
        .toLocaleLowerCase()
        .includes(searchAttributes.toLocaleLowerCase())
    );
  };

  const filteredInstances = getFilteredInstances(failedInstances);
  const filteredInstancesCountLabel =
    filteredInstances.length === 1 ? 'failed instance' : 'failed instances';

  const filteredAttributes = getFilteredAttributes(availableSchemaProperties);

  const handleAddAttribute = async (attributeToAdd: Property) => {
    setShowAttributesList(false);
    setLoadingDataAttributes(true);
    setSearchAttributes('');
    if (!selectedSchema || !project.schemas) return;

    try {
      const query = `
        query getDataAttributesQuery {
          ${convertToSimpleQueryFDM(
            selectedSchema.name,
            [attributeToAdd],
            project.schemas,
            failedInstancesIds
          )}
        }
      `;
      setDataAttributesQuery(query);
    } catch (err: any) {
      notification.error({
        message: 'Failed to fetch data',
        description: err,
      });
    } finally {
      setTableColumnsLabels([...tableColumnsLabels, attributeToAdd.name]);
      const updatedAttributes =
        availableSchemaProperties?.filter(
          (property) => property.name !== attributeToAdd.name
        ) || [];
      setAvailableSchemaProperties(updatedAttributes);
      handleButtonResize();
    }
  };

  const handleRemoveAttribute = (attributeToRemove: Property) => {
    const updatedColumns =
      tableColumnsLabels?.filter(
        (columnName) => columnName !== attributeToRemove.name
      ) || [];
    setTableColumnsLabels(updatedColumns);
    setAvailableSchemaProperties([
      ...availableSchemaProperties,
      attributeToRemove,
    ]);

    const attributeName = attributeToRemove.name;
    failedInstances?.forEach((obj: any) => delete obj[attributeName]);

    handleButtonResize();
  };

  const renderColumnHeader = (columnLabel: string) => (
    <StyledColumnTitle level={6}>
      {addSpaceBeforeCapitalLetters(columnLabel)}
    </StyledColumnTitle>
  );

  const renderFieldValue = (fieldValue: any) => {
    if (!fieldValue) {
      return <div>-</div>;
    }

    if (isObject(fieldValue)) {
      // got an object like this: {items: [{}, {}]}
      const values = Object.values(fieldValue); //  => [0]: [{externalId: "a"}, {externalId: "b"}]
      const parsedValues = flattenJSON(values)[0]; //  => {0.externalId: "a", 1.externalId: "2"}
      const objectFieldValue = Object.values(parsedValues); // => ["a", "b"]

      return (
        <StyledColumnValue>{objectFieldValue.toString()}</StyledColumnValue>
      );
    }
    return <StyledColumnValue>{fieldValue.toString()}</StyledColumnValue>;
  };

  const renderEmptyPlaceholder = () => <StyledEmptyPlaceholder />;

  const renderAddAttributeMenu = () => (
    <StyledMenu>
      {availableSchemaProperties?.length ? (
        <>
          <StyledInput
            placeholder="Search"
            icon="Search"
            iconPlacement="left"
            value={searchAttributes}
            onChange={(e) => setSearchAttributes(e.currentTarget.value)}
          />
          {filteredAttributes.length === 0 ? (
            <InfoMessage type="Search" size={60}>
              No attributes found
            </InfoMessage>
          ) : (
            <StyledAttributesList>
              {sortAttributesAlphabetically(filteredAttributes)?.map(
                (property) => (
                  <Menu.Item onClick={() => handleAddAttribute(property)}>
                    <span>{property.name}</span>
                  </Menu.Item>
                )
              )}
            </StyledAttributesList>
          )}
        </>
      ) : (
        <span>No attributes to add</span>
      )}
    </StyledMenu>
  );

  const renderRemoveAttributeMenu = (removableAttribute: Property) => (
    <StyledMenu key={removableAttribute.id}>
      <Menu.Item onClick={() => handleRemoveAttribute(removableAttribute)}>
        <Button
          type="destructive"
          icon="Remove"
          aria-label="Remove the attribute from table"
          size="small"
        >
          Remove attribute
        </Button>
      </Menu.Item>
    </StyledMenu>
  );

  const renderAddAttributeButton = () => (
    <Button
      onClick={() => setShowAttributesList(!showAttributesList)}
      type="secondary"
      icon="AddLarge"
      aria-label="Open menu for adding an attribute"
    >
      {showLongAddButton ? 'Add attribute' : ''}
    </Button>
  );

  const sortColumnLabels = (labels: string[]) =>
    labels.sort((a) => (a === primaryKeyName ? -1 : 1));

  // Add columns for all the schema attributes
  const tableColumns: ColumnsType<Object> = sortColumnLabels(
    tableColumnsLabels
  ).map((columnLabel) => {
    const removableAttribute = schemaProperties?.find(
      (property) => property.name === columnLabel
    );
    return {
      title: renderColumnHeader(columnLabel),
      key: columnLabel,
      dataIndex: columnLabel,
      width: 160,
      sorter: (a: any, b: any) =>
        (a[columnLabel] || '')
          .toString()
          .localeCompare((b[columnLabel] || '').toString()),
      render: (fieldValue: any) => renderFieldValue(fieldValue),
      filterDropdown: () =>
        removableAttribute && renderRemoveAttributeMenu(removableAttribute),
      filterIcon: () =>
        removableAttribute && (
          <Button
            size="small"
            type="destructive"
            icon="Remove"
            aria-label="Open menu for removing an attribute"
          />
        ),
    };
  });

  // Add last a column for "Adding a new attribute" functionality
  if (failedInstances && failedInstances.length) {
    tableColumns.push({
      title: () => <div id="tableColumn" />,
      key: 'Add attribute',
      dataIndex: 'Add attribute',
      width: '10vw',
      align: 'right',
      render: () => renderEmptyPlaceholder(),
      filterDropdown: () => renderAddAttributeMenu(),
      filterDropdownVisible: showAttributesList,
      onFilterDropdownVisibleChange: (visible) => {
        setShowAttributesList(visible);
      },
      filterIcon: () => renderAddAttributeButton(),
    });
  }

  const renderTableContent = () => {
    if (loadingData) {
      return (
        <CenteredContainer>
          <Spinner />
        </CenteredContainer>
      );
    }
    if (!selectedRun || !failedInstances) {
      return (
        <CenteredContainer>
          <InfoMessage type="Error" title="No data available" />
        </CenteredContainer>
      );
    }

    return (
      <StyledTable>
        <Table
          loading={loadingDataAttributes}
          dataSource={filteredInstances}
          columns={tableColumns}
          scroll={{ y: 'calc(100vh - 22rem)' }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '30', '50', '100'],
          }}
        />
      </StyledTable>
    );
  };

  return (
    <>
      <StyledTitleRow>
        <StyledDetails>
          <Title level={5}>
            {`Run result ${
              selectedRun?.runAt && formatDateRecentTime(selectedRun.runAt)
            }`}
          </Title>
          <Flex alignItems="center" direction="row" gap={10}>
            <Body level={5}>
              {`${
                failedInstances?.length
                  ? filteredInstances.length
                  : totalFailedCount
              } ${filteredInstancesCountLabel}`}
            </Body>
            <RuleRunValidity runStatus={selectedRun} />
          </Flex>
        </StyledDetails>
        <StyledInput
          placeholder="Search…"
          icon="Search"
          iconPlacement="left"
          value={searchInstances}
          onChange={(e) => setSearchInstances(e.currentTarget.value)}
        />
      </StyledTitleRow>

      {renderTableContent()}
      {dataAttributesQuery && (
        <InstancesDataFetch
          dataAttributesQuery={dataAttributesQuery}
          onFetchedData={handleQueryResult}
          schemaCollectionRefId={schemaCollectionRefId}
        />
      )}
    </>
  );
};

const StyledTable = styled.div`
  .ant-table {
    background: transparent;
    margin-bottom: 2rem;
  }

  table {
    border-spacing: 0 4px;
  }
  .ant-table-thead > tr > th,
  .ant-table-tbody > tr > td,
  .ant-table tfoot > tr > th,
  .ant-table tfoot > tr > td {
    padding: 10px;
  }
  .ant-table-thead {
    > tr > th {
      border-bottom: none;
      background: transparent;
    }
  }
  .ant-table-row {
    background: var(--cogs-white);
    box-shadow: var(--cogs-z-4);
    cursor: pointer;
  }
  .ant-table-filter-trigger {
    padding: 0;
  }
  .anticon.ant-table-column-sorter-up,
  .anticon.ant-table-column-sorter-down {
    &.active {
      color: var(--cogs-primary);
    }
  }
`;

const CenteredContainer = styled.div`
  min-height: 50vh;
  position: relative;
`;

const StyledColumnTitle = styled(Title)`
  font-weight: 600;
  text-transform: capitalize;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  max-width: 6vw;
`;

const StyledColumnValue = styled.div`
  padding: 0.5rem 0;
  text-overflow: ellipsis;
  white-space: wrap;
  overflow: auto;
  max-height: 6vh;
`;

const StyledTitleRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

const StyledInput = styled(Input)`
  .cogs-input {
    border-radius: 6px;
  }
  .cogs-icon-Search {
    opacity: 0.25;
    left: 12px;
  }
  .input-clear-button {
    top: calc(50% - 28px / 2);
    right: 4px !important;
    width: 28px;
    height: 28px;
    .cogs-icon.input-content.input-icon.cogs-icon-Close {
      top: calc(50% - 12px / 2);
      width: 12px !important;
      height: 12px !important;
    }

    &:hover {
      cursor: pointer;
    }
  }
`;

const StyledDetails = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.5rem;
`;

const StyledMenu = styled(Menu)`
  max-height: 60vh;
  overflow: hidden;
  ${StyledInput} {
    .cogs-input {
      background-color: var(--cogs-greyscale-grey2);
      font-weight: 500;
    }
    .input-clear-button {
      background: var(--cogs-greyscale-grey2);
    }
  }
`;

const StyledAttributesList = styled.div`
  overflow: auto;
  max-width: 220px;
  span {
    overflow: hidden;
    white-space: wrap;
    text-overflow: ellipsis;
  }
`;

const StyledEmptyPlaceholder = styled.div`
  position: absolute;
  right: 1rem;
  top: calc(50% - 2vh);
  width: 8vw;
  height: 4vh;
  max-width: 120px;
  border: 2px dashed var(--cogs-greyscale-grey4);
  border-radius: 5px;
`;
